import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { cities as citiesApi } from "../../services/api";
import { thumbnailUrl as optimizedThumb } from "../../utils/cloudinary";
import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { calcSimilarity, TIER_SIMILAR } from "../../utils/similarity";
import { ArrowLeft, Loader, ThumbsUp, ThumbsDown, RotateCcw, Zap } from "lucide-react";
import PostPopup from "../Map/PostPopup";
import { useTranslation } from "react-i18next";

const TILE_URLS = {
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  light: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
};

const CATEGORY_COLORS = {
  food: "#f59e0b",
  stay: "#8b5cf6",
  adventure: "#06d6a0",
};

const CATEGORIES = ["food", "stay", "adventure"];
const CATEGORY_LABEL_KEYS = { food: "map.eats", stay: "map.stays", adventure: "map.do" };

function pinSvg(color, bg) {
  return `<svg viewBox="0 0 52 66" fill="none" width="28" height="35"><circle cx="26" cy="22" r="20" fill="${color}"/><circle cx="26" cy="22" r="9" fill="${bg}"/><path d="M26 42 L16 62 L26 56 L36 62 Z" fill="${color}"/></svg>`;
}

const iconCache = new Map();
function getPinIcon(category, theme) {
  const color = CATEGORY_COLORS[category] || "#06d6a0";
  const bg = theme === "dark" ? "#080c14" : "#f4f4f5";
  const key = `city|${category}|${theme}`;
  if (iconCache.has(key)) return iconCache.get(key);
  const icon = L.divIcon({
    className: "",
    iconSize: [28, 35],
    iconAnchor: [14, 35],
    popupAnchor: [0, -35],
    html: pinSvg(color, bg),
  });
  iconCache.set(key, icon);
  return icon;
}

function CityPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [likeYouOnly, setLikeYouOnly] = useState(false);

  const hasStyles = currentUser?.travelStyles?.length > 0;

  const cityName = decodeURIComponent(city);

  useEffect(() => {
    document.title = `${cityName} — Pinned`;
    return () => { document.title = t("app.title"); };
  }, [cityName, t]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await citiesApi.getByName(cityName, category);
        setPosts(data);
      } catch {
        setPosts([]);
      }
      setLoading(false);
    }
    load();
  }, [cityName, category]);

  const blockedIds = currentUser?.blockedIds || [];
  let visiblePosts = blockedIds.length
    ? posts.filter((p) => !blockedIds.includes(p.user?.id))
    : posts;
  if (likeYouOnly && hasStyles) {
    visiblePosts = visiblePosts.filter((p) => {
      const score = calcSimilarity(currentUser.travelStyles, p.user?.travelStyles);
      return score !== null && score >= TIER_SIMILAR;
    });
  }

  // Calculate map bounds from posts
  const bounds = visiblePosts.length > 0
    ? L.latLngBounds(visiblePosts.map((p) => [p.latitude, p.longitude])).pad(0.2)
    : null;

  const countryCode = posts[0]?.countryCode || "";
  const categoryBreakdown = CATEGORIES.map((c) => ({
    key: c,
    label: t(CATEGORY_LABEL_KEYS[c]),
    count: posts.filter((p) => p.category === c).length,
  })).filter((c) => c.count > 0);

  return (
    <div className="h-full flex flex-col bg-th-bg">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="px-4 pt-[max(1rem,env(safe-area-inset-top))] mt-2 pb-3">
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={() => navigate(-1)}
                className="w-9 h-9 bg-th-surface border border-th-border text-th-muted rounded-xl flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-th-text">{cityName}</h1>
                {countryCode && (
                  <p className="text-xs text-th-muted font-mono uppercase tracking-wider">{countryCode}</p>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-3 text-xs text-th-muted font-mono mb-3">
              <span>{t("cities.pinCount", { count: posts.length })}</span>
              {categoryBreakdown.map((c) => (
                <span key={c.key} className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: CATEGORY_COLORS[c.key] }} />
                  {c.count} {c.label.toLowerCase()}
                </span>
              ))}
            </div>

            {/* Category filter */}
            <div className="flex gap-2 mb-3">
              <button
                onClick={() => setCategory(null)}
                className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                  !category ? "bg-th-accent text-th-bg border-th-accent" : "bg-th-surface text-th-muted border-th-border"
                }`}
              >
                {t("map.all")}
              </button>
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(category === c ? null : c)}
                  className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                    category === c ? "bg-th-accent text-th-bg border-th-accent" : "bg-th-surface text-th-muted border-th-border"
                  }`}
                >
                  {t(CATEGORY_LABEL_KEYS[c])}
                </button>
              ))}
              {hasStyles && (
                <button
                  onClick={() => setLikeYouOnly(!likeYouOnly)}
                  className={`inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                    likeYouOnly ? "bg-th-accent text-th-bg border-th-accent" : "bg-th-surface text-th-muted border-th-border"
                  }`}
                >
                  <Zap size={9} />
                  {t("map.travelersLikeYou")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader size={20} className="animate-spin text-th-accent" />
            </div>
          ) : visiblePosts.length === 0 ? (
            <p className="text-center text-sm text-th-muted py-12">{t("profile.noPostsYet")}</p>
          ) : (
            <>
              {/* Map */}
              <div className="h-64 mx-4 rounded-xl overflow-hidden border border-th-border mb-4">
                <MapContainer
                  bounds={bounds}
                  style={{ height: "100%", width: "100%" }}
                  zoomControl={false}
                >
                  <TileLayer
                    url={TILE_URLS[theme] || TILE_URLS.dark}
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                  />
                  {visiblePosts.map((post) => (
                    <Marker
                      key={post.id}
                      position={[post.latitude, post.longitude]}
                      icon={getPinIcon(post.category, theme)}
                    >
                      <Popup className="pinned-popup" closeButton={false} maxWidth={240} minWidth={220}>
                        <PostPopup post={post} user={post.user} />
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>

              {/* Post grid */}
              <div className="px-4 pb-6 grid grid-cols-2 gap-3">
                {visiblePosts.map((post) => (
                  <Link
                    key={post.id}
                    to={`/profile/${post.user?.username || "pinned"}/post/${post.id}`}
                    className="bg-th-card border border-th-border rounded-xl overflow-hidden"
                  >
                    <img
                      src={optimizedThumb(post.thumbnailUrl || post.imageUrl)}
                      alt={post.caption}
                      className="w-full h-32 object-cover"
                      loading="lazy"
                    />
                    <div className="p-2.5">
                      <p className="text-xs text-th-text font-medium line-clamp-2 leading-tight">
                        {post.caption}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        {post.worthIt !== null && post.worthIt !== undefined && (
                          <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${
                            post.worthIt ? "text-emerald-500" : "text-red-400"
                          }`}>
                            {post.worthIt ? <ThumbsUp size={8} /> : <ThumbsDown size={8} />}
                          </span>
                        )}
                        {post.wouldReturn !== null && post.wouldReturn !== undefined && (
                          <span className={`inline-flex items-center gap-0.5 text-[11px] font-mono ${
                            post.wouldReturn ? "text-emerald-500" : "text-amber-500"
                          }`}>
                            <RotateCcw size={8} />
                          </span>
                        )}
                        <span className="text-[11px] text-th-muted font-mono ml-auto">
                          @{post.user?.username}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CityPage;
